import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd, parseIntOrDefault } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Updates PDP quantity via the main `quantity-selector-component`, a `name="quantity"` input, or a synced hidden input.
   * @param {number} direction - 1 to increase, -1 to decrease
   */
  #applyMainQuantityDelta(direction) {
    const productForm = this.#getProductForm();
    if (!productForm || this.refs.addToCartButton?.disabled) return;

    const quantitySelector = productForm.querySelector('quantity-selector-component');

    if (quantitySelector && typeof quantitySelector.updateQuantity === 'function') {
      quantitySelector.updateQuantity(direction);
      return;
    }

    const existingInput = productForm.querySelector('input[name="quantity"]');
    if (existingInput) {
      const min = parseIntOrDefault(existingInput.min, 1);
      const step = parseIntOrDefault(existingInput.step, 1);
      const maxAttr = existingInput.getAttribute('max');
      const max = maxAttr ? parseInt(maxAttr, 10) : null;
      const cartQty = parseIntOrDefault(existingInput.getAttribute('data-cart-quantity'), 0);
      const value = parseIntOrDefault(existingInput.value, min);
      const effectiveMax = max !== null ? Math.max(max - cartQty, min) : null;
      const newValue = Math.min(effectiveMax ?? Infinity, Math.max(min, value + step * direction));

      existingInput.value = String(newValue);
      existingInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, undefined));
      return;
    }

    if (this.dataset.showStickyQuantitySelector !== 'true') return;

    const form = productForm.querySelector('form');
    if (!form) return;

    let hidden = form.querySelector('input[name="quantity"][data-sticky-quantity-sync]');
    if (!hidden) {
      hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'quantity';
      hidden.setAttribute('data-sticky-quantity-sync', '');
      form.appendChild(hidden);
    }

    const min = parseIntOrDefault(this.dataset.quantityMin, 1);
    const step = parseIntOrDefault(this.dataset.quantityStep, 1);
    const max = this.dataset.quantityMax ? parseInt(this.dataset.quantityMax, 10) : null;
    hidden.setAttribute('data-min', String(min));
    hidden.min = String(min);
    hidden.step = String(step);
    if (max) {
      hidden.max = String(max);
    } else {
      hidden.removeAttribute('max');
    }

    const cartQty = parseIntOrDefault(hidden.getAttribute('data-cart-quantity'), 0);
    const value = parseIntOrDefault(hidden.value, min);
    const effectiveMax = max !== null ? Math.max(max - cartQty, min) : null;
    const newValue = Math.min(effectiveMax ?? Infinity, Math.max(min, value + step * direction));

    hidden.value = String(newValue);
    hidden.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, undefined));
  }

  /**
   * Keeps the hidden quantity input (if any) aligned after variant changes.
   */
  #syncStickyQuantityHiddenInput() {
    if (this.dataset.showStickyQuantitySelector !== 'true') return;
    const productForm = this.#getProductForm();
    const hidden = productForm?.querySelector('input[name="quantity"][data-sticky-quantity-sync]');
    if (!(hidden instanceof HTMLInputElement)) return;

    const min = parseIntOrDefault(this.dataset.quantityMin, 1);
    const step = parseIntOrDefault(this.dataset.quantityStep, 1);
    hidden.min = String(min);
    hidden.step = String(step);
    if (this.dataset.quantityMax) {
      hidden.max = this.dataset.quantityMax;
    } else {
      hidden.removeAttribute('max');
    }

    let next = parseIntOrDefault(this.dataset.initialQuantity, min);
    const max = this.dataset.quantityMax ? parseInt(this.dataset.quantityMax, 10) : null;
    if (max !== null) {
      next = Math.min(next, max);
    }
    if ((next - min) % step !== 0) {
      next = min + Math.floor((next - min) / step) * step;
    }
    hidden.value = String(next);
    hidden.dispatchEvent(new QuantitySelectorUpdateEvent(next, undefined));
  }

  handleStickyQtyIncrease = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    this.#applyMainQuantityDelta(1);
  };

  handleStickyQtyDecrease = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    this.#applyMainQuantityDelta(-1);
  };

  /** Clicks the main PDP add-to-cart control (puppet) so the real form submission runs. */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore quantity-related host attributes from refreshed markup (variant rules may change)
    for (const attr of newStickyAddToCart.attributes) {
      if (
        attr.name === 'data-show-sticky-quantity-selector' ||
        attr.name === 'data-quantity-min' ||
        attr.name === 'data-quantity-step' ||
        attr.name === 'data-quantity-max' ||
        attr.name === 'data-initial-quantity'
      ) {
        this.setAttribute(attr.name, attr.value);
      }
    }
    if (!newStickyAddToCart.hasAttribute('data-quantity-max')) {
      this.removeAttribute('data-quantity-max');
    }
    if (!newStickyAddToCart.hasAttribute('data-show-sticky-quantity-selector')) {
      this.removeAttribute('data-show-sticky-quantity-selector');
    }

    // Refs inside the morphed bar refresh on the next microtask (MutationObserver).
    queueMicrotask(() => {
      this.#currentQuantity = parseIntOrDefault(this.dataset.initialQuantity, 1);
      this.#syncStickyQuantityHiddenInput();
      this.#updateButtonText();
    });
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */(option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    const stickyQtyValue = /** @type {HTMLElement | undefined} */ (this.refs.stickyQtyValue);
    if (stickyQtyValue) {
      stickyQtyValue.textContent = this.#currentQuantity.toString();
    }

    this.#updateStickyQtyButtonStates();

    const showStickyQuantity = this.dataset.showStickyQuantitySelector === 'true';
    if (showStickyQuantity) {
      quantityDisplay.style.display = 'none';
      return;
    }

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }

  /**
   * Disables +/- when at min / effective max (matches main PDP quantity selector).
   */
  #updateStickyQtyButtonStates() {
    const minus = /** @type {HTMLButtonElement | undefined} */ (this.refs.stickyQtyMinus);
    const plus = /** @type {HTMLButtonElement | undefined} */ (this.refs.stickyQtyPlus);
    if (!minus || !plus) return;

    const productForm = this.#getProductForm();
    const quantitySelector = productForm?.querySelector('quantity-selector-component');

    if (quantitySelector && typeof quantitySelector.getCurrentValues === 'function') {
      const { min, value } = quantitySelector.getCurrentValues();
      const effectiveMax =
        typeof quantitySelector.getEffectiveMax === 'function' ? quantitySelector.getEffectiveMax() : null;

      const disableAll = Boolean(this.refs.addToCartButton?.disabled);
      minus.disabled = disableAll || value <= min;
      plus.disabled = disableAll || (effectiveMax !== null && value >= effectiveMax);
      return;
    }

    const input = productForm?.querySelector('input[name="quantity"]');
    if (input) {
      const min = parseIntOrDefault(input.min, 1);
      const step = parseIntOrDefault(input.step, 1);
      const maxAttr = input.getAttribute('max');
      const max = maxAttr ? parseInt(maxAttr, 10) : null;
      const cartQty = parseIntOrDefault(input.getAttribute('data-cart-quantity'), 0);
      const value = parseIntOrDefault(input.value, min);
      const effectiveMax = max !== null ? Math.max(max - cartQty, min) : null;
      const disableAll = Boolean(this.refs.addToCartButton?.disabled);

      minus.disabled = disableAll || value <= min;
      plus.disabled = disableAll || (effectiveMax !== null && value >= effectiveMax);
      return;
    }

    const min = parseIntOrDefault(this.dataset.quantityMin, 1);
    const max = this.dataset.quantityMax ? parseInt(this.dataset.quantityMax, 10) : null;
    const value = this.#currentQuantity;
    const cartQty = 0;
    const effectiveMax = max !== null ? Math.max(max - cartQty, min) : null;
    const disableAll = Boolean(this.refs.addToCartButton?.disabled);

    minus.disabled = disableAll || value <= min;
    plus.disabled = disableAll || (effectiveMax !== null && value >= effectiveMax);
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
